import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type {
	IBuilderComponentLoader,
	BuilderComponentRegistrar,
	IBuilderComponentPageConfig,
	ComponentLoaderFunction,
	BuilderComponentResolver,
} from './types'
import { BuilderComponentLoaderSymbol, BuilderComponentResolverSymbol, name } from './symbols'
import type { IComponentsLoader } from '@wix/thunderbolt-components-loader'
import { ComponentsLoaderSymbol } from '@wix/thunderbolt-components-loader'
import type { BrowserWindow } from '@wix/thunderbolt-symbols'
import { BrowserWindowSymbol } from '@wix/thunderbolt-symbols'
import { resolveComponentUrl } from './utils'
import { isSSR } from '@wix/thunderbolt-commons'

export const builderComponentRegistrar = withDependencies(
	[ComponentsLoaderSymbol, BuilderComponentLoaderSymbol, BuilderComponentResolverSymbol, BrowserWindowSymbol],
	(
		componentsLoader: IComponentsLoader,
		builderComponentsLoader: IBuilderComponentLoader,
		{ getBuilderComponentResources }: BuilderComponentResolver,
		browserWindow: BrowserWindow
	): BuilderComponentRegistrar => {
		const builderComponentRegisterCache = new Map<string, Promise<void> | true>()
		const builderComponentLoaderCache = new Map<string, ComponentLoaderFunction>()
		const builderComponentLoaderResultCache = new Map<string, ReturnType<ComponentLoaderFunction>>()

		return {
			loadAndRegisterComponent: async (builderType: string, isEditor: boolean, compDescendants: Array<string>) => {
				// Check if this builderType is already being loaded or has been loaded
				const cachedPromise = builderComponentRegisterCache.get(builderType)
				if (cachedPromise) {
					await cachedPromise
					return
				}

				const builderTypesToLoad = [builderType, ...compDescendants]

				// Create a loader for the root component and its descendants
				const builderLoaders = builderTypesToLoad.map((builderType) => {
					const cachedLoader = builderComponentLoaderCache.get(builderType)
					if (cachedLoader) {
						return { builderType, loader: cachedLoader }
					}
					// resolve resources for each builder type separately
					const {
						appDefinitionId,
						componentId: builderComponentId,
						componentUrl: clientUrl,
						editorComponentUrl,
						serverUrl,
					} = getBuilderComponentResources({ builderType })
					const resolvedComponentUrl = resolveComponentUrl(
						isEditor,
						isSSR(browserWindow),
						clientUrl,
						editorComponentUrl,
						serverUrl
					)
					const loader = builderComponentsLoader.getComponentLoader({
						componentUrl: resolvedComponentUrl,
						builderComponentId,
						builderType,
						appDefinitionId,
					})
					builderComponentLoaderCache.set(builderType, loader)
					return {
						builderType,
						loader,
					}
				})

				// create a wrapper loader for the root component, which will load all the components (root + descendants) and then return just the root component loader result
				const rootComponentLoader = async () => {
					const loadersResults = await Promise.all(
						builderLoaders.map(async ({ loader, builderType }) => {
							let loaderResultPromise = builderComponentLoaderResultCache.get(builderType)
							if (!loaderResultPromise) {
								loaderResultPromise = loader()
								builderComponentLoaderResultCache.set(builderType, loaderResultPromise)
							}
							return await loaderResultPromise
						})
					)
					// return the loader result for the root component
					return loadersResults[0]
				}

				// register only the root component loader
				const registerPromise = componentsLoader.registerComponent(builderType, rootComponentLoader)

				// Cache the promise so concurrent calls wait for the same load
				builderComponentRegisterCache.set(builderType, registerPromise)

				try {
					await registerPromise
					builderComponentRegisterCache.set(builderType, true) // performance optimization - we don't need to cache the promise if it's already been loaded
				} catch (error) {
					// Remove from cache on failure to allow retry
					builderComponentRegisterCache.delete(builderType)
					throw error
				}
			},
			clearBuilderComponentLoadCache: (builderType: string) => {
				builderComponentRegisterCache.delete(builderType)
			},
		}
	}
)
